import React, { useState, useRef } from 'react';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable'; // Import the plugin for jsPDF
import IconButton from '../../elements/IconButton';
import Button from '../../elements/Button';

const Main = (props) => {
    const [loadedData, setLoadedData] = useState([]);
    const [stillGettingData, setStillGettingData] = useState(0);
    const isCancelled = useRef(false); // Used to track if fetching was cancelled

    const handleCancelFetch = () => {
        isCancelled.current = true; // Set flag to true when fetching is cancelled
    };

    const fetchAllData = (exportType) => {
        let skip = 0;
        const limit = 15;
        let allData = [];
        setStillGettingData(1);

        const fetchNext = () => {
            if (isCancelled.current) {
                console.log("Fetching has been cancelled by the user.");
                setStillGettingData(0); // Update state to reflect cancellation
                return;
            }
            getData(skip, limit, (hasMoreData, newData) => {
                if (hasMoreData && newData) {
                    allData = allData.concat(newData);
                    skip += limit;
                    fetchNext();
                } else {
                    if (exportType === 'CSV') {
                        downloadCSV(allData, props.exportKeys, props.fileTitle);
                    } else if (exportType === 'PDF') {
                        downloadPDF(allData, props.exportKeys, props.fileTitle);
                    }
                    setStillGettingData(2);
                }
            });
        };

        fetchNext();
    };

    const getData = (skip, limit, onCompletion) => {
        var myHeaders = new Headers();
        myHeaders.append("Accept", "application/json");
        myHeaders.append("Authorization", "Bearer " + localStorage.getItem('access_token'));
        myHeaders.append("Content-Type", "application/json");

        var requestOptions = {
            method: 'POST',
            headers: myHeaders,
            redirect: 'follow',
            body: JSON.stringify(props.filterData)
        };

        props.tokenSafeAPIRequest(
            `${props.url}${props.specific ? '/' + props.specific : ''}?limit=${limit}&fetch_links=true&skip=${skip}&sort_field=${props.sort}&start_date=${props.calStartDate}&end_date=${props.calEndDate}&active_only=false`,
            requestOptions,
            '',
            (result, status) => {
                if ('postProcessing' in props) {
                    result = props.postProcessing(result);
                }

                if ('reports' in result) {
                    processResult(result.reports, onCompletion);
                } else if ('accounts' in result) {
                    processResult(result.accounts, onCompletion);
                } else if ('transactions' in result) {
                    processResult(result.transactions, onCompletion);
                } else if ('dealerships' in result) {
                    processResult(result.dealerships, onCompletion);
                } else if ('spiffs' in result) {
                    processResult(result.spiffs, onCompletion);
                } else if ('dppps_and_gap' in result) {
                    processResult(result.dppps_and_gap, onCompletion);
                } else {
                    processResult(result, onCompletion);
                }
            }
        );
    };

    const processResult = (data, onCompletion) => {
        if (data && data.length > 0) {
            setLoadedData((loadedData) => [...loadedData, ...data]);
            onCompletion(true, data);
        } else {
            onCompletion(false);
        }
    };

    const downloadCSV = (data, keys, filename = 'data.csv') => {
        const flattenObject = (obj, parentKey = '', res = {}) => {
            for (let key in obj) {
                let propName = parentKey ? parentKey + '.' + key : key;
                if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
                    flattenObject(obj[key], propName, res);
                } else {
                    res[propName] = obj[key];
                }
            }
            return res;
        };

        // Properly handle commas by enclosing values in double quotes
        data = data.map(item => {
            const flatItem = flattenObject(item);
            for (let key in flatItem) {
                if (typeof flatItem[key] === 'string') {
                    flatItem[key] = `${flatItem[key].replace(/"/g, '"')}`; // Escape double quotes and wrap in quotes
                }
            }
            return flatItem;
        });

        let csvContent = keys.map(key => key.label).join(',') + '\n';
        data.forEach(item => {
            const flatItem = flattenObject(item);
            const row = keys.map(key => {
                let value = flatItem[key.id] ? flatItem[key.id].toString() : '';
                return `"${value.replace(/"/g, '""')}"`;
            }).join(',');
            csvContent += row + '\n';
        });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        if (navigator.msSaveBlob) {
            navigator.msSaveBlob(blob, filename);
        } else {
            const link = document.createElement('a');
            if (link.download !== undefined) {
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', filename);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        }
    };

    const formatMoney = (value) => {
        const num = Number(value);
        if (!Number.isFinite(num)) return "$0.00";

        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(num);
    };

    const downloadPDF = (data, keys, filename = "data.pdf") => {
        const doc = new jsPDF();

        const addPageNumbers = (doc) => {
            const pageSize = doc.internal.pageSize;
            const pageHeight = pageSize.height;
            const pageWidth = pageSize.width;

            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.text(`Page ${i}`, pageWidth - 30, pageHeight - 10);
            }
        };

        doc.text(props.fileTitle || "Report", 20, 10);

        const flattenObject = (obj, parentKey = "", res = {}) => {
            for (let key in obj) {
            const propName = parentKey ? parentKey + "." + key : key;
            if (typeof obj[key] === "object" && obj[key] !== null && !Array.isArray(obj[key])) {
                flattenObject(obj[key], propName, res);
            } else {
                res[propName] = obj[key];
            }
            }
            return res;
        };

        const flattenedData = data.map((item) => flattenObject(item));

        const rows = flattenedData.map((item) =>
            keys.map((key, index) => {
            const value = item[key.id] ?? "";
            return index === 4 ? formatMoney(value) : value;
            })
        );
        const headers = keys.map((key) => key.label);

        const GREEN = [210, 245, 210];
        const BLUE = [210, 210, 245];
        const YELLOW = [245, 245, 210];

        // indices per your spec
        const ID_COL_INDEX = 1;
        const NAME_COL_INDEX = 2;
        const MAIL_CHOICE_COL_INDEX = 5; // must equal "Subagent Address"

        // Precompute duplicate IDs
        const idCounts = new Map();
        for (const rowObj of flattenedData) {
            const id = rowObj?.[keys[ID_COL_INDEX]?.id];
            if (id == null) continue;
            const k = String(id).trim();
            idCounts.set(k, (idCounts.get(k) || 0) + 1);
        }

        const isSubagentAddressChosen = (rowObj) => {
            const v = String(rowObj?.[keys[MAIL_CHOICE_COL_INDEX]?.id] ?? "").trim();
            return v !== "Dealership Address" && v !== "Spiffee Address";
        };

        const isDuplicateId = (rowObj) => {
            const id = rowObj?.[keys[ID_COL_INDEX]?.id];
            if (id == null) return false;
            return (idCounts.get(String(id).trim()) || 0) > 1;
        };

        const nameHasParenAndNo = (rowObj) => {
            const name = rowObj?.[keys[NAME_COL_INDEX]?.id];
            const s = String(name ?? "");
            const hasParen = /[()]/.test(s);
            const hasNoSpace = s.toLowerCase().includes("no ");
            return hasParen && hasNoSpace;
        };

        const hasentReceivedACheck = (rowObj) => {
            return !rowObj.received_check;
        }

        const shouldHighlightRow = (rowObj) =>
            isSubagentAddressChosen(rowObj) || isDuplicateId(rowObj) || nameHasParenAndNo(rowObj);

        doc.autoTable({
            head: [headers],
            body: rows,
            // IMPORTANT: only touch body cells; and only on matching rows
            didParseCell: (hook) => {
            if (hook.section !== "body") return;

            const rowIndex = hook.row.index; // 0-based index within body
            const rowObj = flattenedData[rowIndex];
            if (!rowObj) return;

            if (shouldHighlightRow(rowObj)) {
                if (hasentReceivedACheck(rowObj))
                {
                    hook.cell.styles.fillColor = BLUE;
                }
                else
                {
                    hook.cell.styles.fillColor = GREEN;
                }
            }
            else
            {
                if (hasentReceivedACheck(rowObj)) {
                    hook.cell.styles.fillColor = YELLOW;
                }
            }
            },
        });

        let total = 0;
        flattenedData.forEach((item) => {
            const value = Number(item[keys[4].id]);
            if (Number.isFinite(value)) total += value;
        });

        doc.text("Total: " + formatMoney(total), 20, doc.lastAutoTable.finalY + 10);

        addPageNumbers(doc);
        doc.save(filename);
    };


    return (
        <div style={{ width: '360px', height: 'auto', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flexBasis: '10%', display: 'flex', flexDirection: 'row' }}>
                <span style={{ fontWeight: '500', fontSize: '20px', flexBasis: '99%' }}>Export Report</span>
                <IconButton img={false} colors={props.colors} Style={{ borderRadius: '3px', backgroundColor: props.colors.border, width: '24px' }} size="16px">
                    <div onClick={() => { handleCancelFetch(); props.setIsOpen() }} style={{ paddingLeft: '3px', paddingRight: '3px' }}><span onClick={() => { handleCancelFetch(); props.setIsOpen() }}>x</span></div>
                </IconButton>
            </div>
            <div style={{ flexBasis: '90%', display: 'flex', flexDirection: 'column', marginTop: '20px' }}>
                <span style={{ fontWeight: '500', fontSize: '16px' }}>Downloaded Rows: {loadedData.length}</span>
                <Button colors={props.colors} active={true} Style={{ marginTop: '10px' }} onClick={() => fetchAllData('CSV')}>Download CSV</Button>
                <Button colors={props.colors} active={true} Style={{ marginTop: '10px' }} onClick={() => fetchAllData('PDF')}>Download PDF</Button>
            </div>
        </div>
    );
};

export default Main;
